import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  TextField,
  Button,
  Divider,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { NotificationManager } from "react-notifications";
import CommonLoader from "../common/CommonLoader";
import {
  GetQuoteTransactionById,
  SaveQuoteTransaction,
  UpdateQuoteTransaction,
} from "../../services/QuotesService";

const AddEditQuoteTransactionDialog = ({
  open,
  onClose,
  quoteNumber,
  recordId = null,
  onSaveComplete,
}) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    quoteNumber: "",
    sampleNumber: 0,
    itemNumber: 0,
    testCode: "",
    description: "",
    quantity: 0,
    unitPrice: 0,
    listPrice: 0,
    adjustedPrice: 0,
    discountPercent: 0,
    surchargePercent: 0,
    clientJobNumber: "",
    globalJobNumber: "",
    miscChg: false,
    methodCode: "",
    applyDiscount: false,
    recordid: 0,
    frequency: "",
    detail: "",
  });

  useEffect(() => {
    if (open) {
      if (recordId) {
        fetchQuoteTransaction();
      } else {
        // Reset form for new transaction
        setFormData({
          quoteNumber: quoteNumber || "",
          sampleNumber: 0,
          itemNumber: 0,
          testCode: "",
          description: "",
          quantity: 0,
          unitPrice: 0,
          listPrice: 0,
          adjustedPrice: 0,
          discountPercent: 0,
          surchargePercent: 0,
          clientJobNumber: "",
          globalJobNumber: "",
          miscChg: false,
          methodCode: "",
          applyDiscount: false,
          recordid: 0,
          frequency: "",
          detail: "",
        });
        setErrors({});
      }
    }
  }, [open, recordId, quoteNumber]);

  const fetchQuoteTransaction = async () => {
    setLoading(true);
    try {
      const response = await GetQuoteTransactionById(quoteNumber, recordId);
      if (response && response.results) {
        setFormData(response.results);
      } else {
        NotificationManager.error("Failed to load transaction data.");
      }
    } catch {
      NotificationManager.error("Error fetching transaction.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (e) => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.testCode) newErrors.testCode = "Test Code is required";
    if (!formData.listPrice) newErrors.listPrice = "List Price is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      let response;
      if (recordId) {
        response = await UpdateQuoteTransaction(formData);
      } else {
        response = await SaveQuoteTransaction(formData);
      }

      if (response.status === 200 || response.statusCode === 200) {
        NotificationManager.success(
          recordId
            ? "Transaction updated successfully"
            : "Transaction added successfully"
        );
        if (onSaveComplete) {
          onSaveComplete();
        }
        onClose();
      } else {
        throw new Error("Failed to save transaction.");
      }
    } catch (err) {
      NotificationManager.error(err.message || "Error saving transaction.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="quote-transaction-dialog-title"
    >
      <DialogTitle id="quote-transaction-dialog-title">
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          {recordId ? "Edit Quote Transaction" : "Add Quote Transaction"}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          disabled={loading}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Test Code"
              value={formData.testCode}
              onChange={handleChange("testCode")}
              fullWidth
              size="small"
              required
              error={!!errors.testCode}
              helperText={errors.testCode}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Description"
              value={formData.description}
              onChange={handleChange("description")}
              fullWidth
              size="small"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Quantity"
              type="number"
              value={formData.quantity}
              onChange={handleChange("quantity")}
              fullWidth
              size="small"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="List Price"
              type="number"
              value={formData.listPrice}
              onChange={handleChange("listPrice")}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
              error={!!errors.listPrice}
              helperText={errors.listPrice}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Adjusted Price"
              type="number"
              value={formData.adjustedPrice}
              onChange={handleChange("adjustedPrice")}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Discount %"
              type="number"
              value={formData.discountPercent}
              onChange={handleChange("discountPercent")}
              fullWidth
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">%</InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Unit Price"
              type="number"
              value={formData.unitPrice}
              onChange={handleChange("unitPrice")}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Method Code"
              value={formData.methodCode}
              onChange={handleChange("methodCode")}
              fullWidth
              size="small"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl variant="outlined" fullWidth size="small">
              <InputLabel>Frequency</InputLabel>
              <Select
                value={formData.frequency || ""}
                onChange={handleChange("frequency")}
                label="Frequency"
              >
                <MenuItem value="">
                  <em>Select Frequency</em>
                </MenuItem>
                <MenuItem value="as needed">as needed</MenuItem>
                <MenuItem value="daily">daily</MenuItem>
                <MenuItem value="bi-weekly">bi-weekly</MenuItem>
                <MenuItem value="weekly">weekly</MenuItem>
                <MenuItem value="monthly">monthly</MenuItem>
                <MenuItem value="quarterly">quarterly</MenuItem>
                <MenuItem value="annually">annually</MenuItem>
                <MenuItem value="semi-annually">semi-annually</MenuItem>
                <MenuItem value="every 3 years">every 3 years</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Detail"
              value={formData.detail}
              onChange={handleChange("detail")}
              fullWidth
              size="small"
              multiline
              rows={3}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.miscChg || false}
                  onChange={handleChange("miscChg")}
                />
              }
              label="Misc Charge"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.applyDiscount || false}
                  onChange={handleChange("applyDiscount")}
                />
              }
              label="Apply Discount"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={handleClose}
          color="secondary"
          variant="outlined"
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={loading}
        >
          {recordId ? "Update" : "Save"}
        </Button>
      </DialogActions>

      <CommonLoader loading={loading} />
    </Dialog>
  );
};

export default AddEditQuoteTransactionDialog;